/**
 * Scenario and Scene types for database-backed scenario management
 */

/**
 * 発話単位話者帰属 (`scenes.dialogue_lines`)。
 * docs/features/scenario-dialogue-speaker-plan.md §3.1。
 *
 * - `actor_id` が非 null のとき `actor_kind` は必須 ('actor' = AI俳優 /
 *   'seedance_preset' = Seedance プリセット俳優)。voice_binding の kind 語彙と揃える。
 * - `dialogue_lines` が NULL のシーン = レガシーモード (フラット `dialogue` が正本)。
 *   `[]` は「構造化モード・セリフなし」であり NULL とは区別する (潰さないこと)。
 */
export type DialogueLineActorKind = 'actor' | 'seedance_preset';

export type DialogueLineSpeaker = {
  /** 声の台本 (script_contract='motion-v2') のナレーター行だけ 'narration'
   *  (`{kind:'narration', label:'ナレーター', actor_id:null, actor_kind:null}`)。
   *  docs/features/brand-cut-motion-graphics-plan.md §10.7.2。それ以外の話者は持たない。 */
  kind?: 'narration';
  label: string;
  actor_id: string | null;
  actor_kind: DialogueLineActorKind | null;
  /** 登場人物宣言 (`generation_metadata.characters`) への参照キー (任意)。
   *  docs/features/scenario-telop-speaker-plan.md §3.9。 */
  character_key?: string | null;
};

/**
 * テロップ行ごとの話者割り当て (`scenes.telop_lines`)。
 * docs/features/scenario-telop-speaker-plan.md §3.1。
 *
 * - `speaker` の語彙は動画エディタの CaptionSpeaker と 1:1 (kind = actor | narration | other)。
 *   `null` は話者なし (キャッチコピー・商品名・CTA 等)。
 * - `telop_lines` が NULL のシーン = レガシーモード (フラット `telop` が正本・エディタは文言一致で推定)。
 *   `[]` は「構造化モード・テロップなし」で NULL とは区別する。
 * - フラット `telop` は `telop_lines[].text` を改行で連結した BE 導出のミラー (添字が一致する)。
 */
export type TelopLineSpeakerKind = 'actor' | 'narration' | 'other';

export type TelopLineSpeaker = {
  kind: TelopLineSpeakerKind;
  label: string | null;
  actor_id: string | null;
  actor_kind: DialogueLineActorKind | null;
  character_key?: string | null;
};

export type TelopLine = {
  text: string;
  speaker: TelopLineSpeaker | null;
  /** 「合わせる声」= 同じシーンの dialogue_lines[].line_id の配列 (声の台本 motion-v2・§10.7.2)。
   *  無ければ声に合わせない文字 (見出し・数字・CTA)。存在しない line_id を送ると BE が 400。
   *  2026-09-29 以降は UI で扱わずレンダラーにも渡さない (テロップは動画エディタで重ねる)。2026-09-30 以降は AI が
   *  写した声の行を書き、動画エディタが字幕の時刻合わせに使う (mapper/subtitleTiming.ts alignSubtitleWindowsByVoiceRef)。 */
  voice_ref?: string[];
};

/** 登場人物宣言 (`generation_metadata.characters`)。キャスト俳優以外で一意に識別する人物。 */
export type ScenarioCharacter = {
  key: string;
  label: string;
  description?: string;
  appears_on_screen?: boolean;
};

/**
 * キャスト俳優の役柄名 (`generation_metadata.cast_roles`)。シナリオ生成AIが識別子 (A, B, …)
 * ごとに決めた役割・呼び名。俳優の表示名は利用者がキャストを選ぶためのもので、シナリオ
 * 以降の話者表示には役柄名を使う (docs/features/scenario-cast-role-names-plan.md)。
 * `actor_id` は生成時点の俳優 (入れ替え後は古くなり得るので表示の照合は letter で行う)。
 */
export type ScenarioCastRole = {
  letter: string;
  actor_id?: string | null;
  actor_kind?: DialogueLineActorKind | null;
  role: string;
};

export const TELOP_LINE_TEXT_MAX_CHARS = 100;

/** 声の行の読み手 (§10.7.3)。tts = 声の共通層で読む / video_ai = 動画 AI が映像と一緒に発話する。 */
export type DialogueVoiceSource = 'tts' | 'video_ai';

export type DialogueLine = {
  /** 声の行の安定識別子 (8 桁の 16 進)。telop_lines[].voice_ref が指す。BE が保存時に無い行へ付ける
   *  (FE が新しい行に先に付けてもよい)。並べ替え・編集で変えない。 */
  line_id?: string;
  speaker: DialogueLineSpeaker;
  text: string;
  voice_source?: DialogueVoiceSource;
  /** 行単位の読み方の上書き (未決 B・今回の UI では編集しない。あれば保つ)。 */
  style?: string;
  /** 声の生成から外す行 (BE は受け付ける・今回の UI では編集しない。あれば保つ)。 */
  excluded?: boolean;
};

// ハード制約 (backend/services/scenario/utils/mention_tokens.py と同値の軽い入力ガード用。
// 正本は BE の normalize_dialogue_lines(strict=True) / 422 判定)。行数の上限は無い (2026-10-17 撤廃)。
export const DIALOGUE_LINE_TEXT_MAX_CHARS = 100;
// Seedance プリセットデジタルキャラクター名がそのまま label に入るため 50 (BE と同値)
export const DIALOGUE_LINE_LABEL_MAX_CHARS = 50;

/** 場面固有化 (P1) のシナリオ単位「場面」宣言。
 *  DB 上は `scenarios.generation_metadata.locations` (JSONB) に保存され、`Scenario` 型には
 *  トップレベルのキーとして現れない (BE レスポンスに合わせて generation_metadata 経由で読む)。
 *  `key` は追加時に確定する不変 ID。`Scene.location_key` がこの `key` を参照する。
 *  docs/features/scene-location-and-production-mode-plan.md §1.4/§2.1/§2.4。 */
export type ScenarioLocation = {
  key: string;
  name: string;
  description: string;
};

/** ストーリーボードモードの絵コンテ候補画像 */
export interface Storyboard {
  id: string;
  scenario_id: string;
  batch_id: string;
  candidate_index: number;
  // シナリオ内で単調増加するレター席 (A=0, B=1, ...)。削除されても残り行のレターは不変。
  letter_index?: number | null;
  material_id?: string | null;
  image_url?: string | null;
  thumbnail_url?: string | null;
  is_selected: boolean;
  image_model?: string | null;
  aspect_ratio?: string | null;
  created_at: string;
  // 生成時のメタデータ。avoid_faces が true の場合、絵コンテの登場人物は顔をマネキン化
  // して描画されており、動画生成時には改めて顔が描かれる旨をフロントで案内する。
  generation_metadata?: {
    model?: string | null;
    image_size?: string | null;
    reference_image_count?: number | null;
    frame_mode?: string | null;
    avoid_faces?: boolean | null;
    // イラスト絵コンテで生成された場合 true。完成動画は実写になる旨をフロントで案内する。
    illustration_storyboard?: boolean | null;
    source?: string | null;
    original_filename?: string | null;
  } | null;
}

/** 元動画のフレームレート (ffprobe の r_frame_rate を有理数で。取れなければ 24/1)。 */
export interface VideoFpsRational {
  num: number;
  den: number;
}

/**
 * 絵コンテの境界の正本 `scenes.generation_metadata.span_override` (§10.13.1 契約 B)。
 * 担当カードの動画 (base_material_id) の中でこのシーンが占める [in_frame, out_frame) を、保存時の版情報
 * (card_scene_id / card_range / segment_frames) と一緒に持つ。読み出し時は版が今と揃うときだけ適用する
 * (features/scenarios/storyboardSpans.ts の resolveSegmentSpans)。
 */
export interface SceneSpanOverride {
  in_frame: number;
  out_frame: number;
  fps: VideoFpsRational;
  base_material_id: string;
  card_scene_id: string;
  card_range: { start: number; end: number };
  segment_frames: number;
  saved_at: string | null;
}

/**
 * 絵コンテのモーション上書きの結果 `scenes.generation_metadata.motion_overlay` (§10.13.1 契約 C)。
 * 生成した動画素材 (material_id) と、受付時に固定した生成時の区間 (generated_span)。stale は保存せず
 * 読み出し時に現在の区間と比較して決める (storyboardSpans.ts の resolveMotionOverlay)。
 */
export interface SceneMotionOverlay {
  material_id: string;
  job_id: string | null;
  generated_at: string | null;
  /** 効果音スイッチ (既定 false = 上書きは映像のみ・無音)。 */
  mix_sound: boolean;
  /** 「テロップを表示」(既定 false = 上書き区間の絵コンテのテロップを隠す。モーション側の画面の文字と競合するため)。 */
  show_telop: boolean;
  /** 「利用しない」(元の映像に戻る)。 */
  disabled: boolean;
  generated_span: {
    base_material_id: string;
    card_scene_id: string;
    card_range: { start: number; end: number };
    in_frame: number;
    out_frame: number;
    fps: VideoFpsRational;
    duration_sec: number;
  };
}

/**
 * フレーム生成の参照画像の抜き差し (docs/features/scene-frame-generation-tuning-plan.md §6.3)。
 * exclude / include の要素は参照キー (material:<id> / casting:<model_id> / prev_frame)、added は素材 ID。
 */
export interface SceneFrameReferenceOverrides {
  exclude?: string[];
  include?: string[];
  added?: string[];
}

export interface Scene {
  id: string;
  scenario_id: string;
  order_number: number;
  setting: string;
  script: string;
  narration: string;
  dialogue: string;
  // 発話単位話者帰属。NULL=レガシー(フラット dialogue が正本) / 配列([] 含む)=構造化モード。
  // docs/features/scenario-dialogue-speaker-plan.md §3.1/§3.7。
  dialogue_lines?: DialogueLine[] | null;
  telop: string;
  /** テロップ行ごとの話者割り当て。NULL/未定義=レガシー (telop が正本)・[]=構造化でテロップなし。 */
  telop_lines?: TelopLine[] | null;
  telop_in_frame: 'include' | 'exclude' | 'dialogue';
  visual: string;
  image_url?: string;
  image_source_url?: string;
  image_material_id?: string;
  video_material_id?: string;
  include_product?: boolean;
  // ブランドカット位置: 'start'(タイトルカード) / 'end'(エンドカード) / undefined(通常シーン)。
  brand_cut_position?: 'start' | 'end' | null;
  // ブランドカットの表示秒数 (1-10)。brand_cut_position があるシーンでのみ有効。NULL は表示時3秒。
  brand_cut_duration_seconds?: number | null;
  /** シーン単位のフレーム生成の追加プロンプト (最初のフレームのみ)。NULL=なし。
   *  docs/features/scene-frame-generation-tuning-plan.md §5。 */
  frame_additional_prompt?: string | null;
  /** フレーム生成の参照画像の抜き差し。NULL=なし (同 §5・§6.3)。 */
  frame_reference_overrides?: SceneFrameReferenceOverrides | null;
  // 場面固有化 (P1): シナリオの locations 宣言 (下記 ScenarioLocation) を参照する key。
  // 未宣言・旧シナリオでは null/undefined (従来通り setting 自由テキストのみで挙動)。
  // docs/features/scene-location-and-production-mode-plan.md §1.4/§2.4。
  location_key?: string | null;
  continues_previous_shot?: boolean;
  /** シーン内に人物の顔が映るか (LLM 判定+ユーザー訂正)。欠損は顔あり扱い (安全側)。
   *  Seedance×フレームモードのイラスト化/r2v 切替 (faceSafe) に使う。 */
  has_visible_face?: boolean;
  preset_image_url?: string;
  preset_image_material_id?: string;
  image_prompt?: string;
  video_prompt?: string;
  // 絵コンテでは span_override (SceneSpanOverride) / motion_overlay (SceneMotionOverlay) もここに入る
  // (読み出しは storyboardSpans.ts の parseSpanOverride / parseMotionOverlay で形を検証する)。
  generation_metadata?: Record<string, unknown>;
  last_frame_url?: string;
  last_frame_material_id?: string;
  /** 生成動画 (video_material_id の現在のテイク) の末尾フレーム素材。get_scenario が
   *  動画素材の generation_metadata.provider_returned_last_frame_material_id から導出する
   *  読み取り専用の値で、ユーザーの「最後のフレーム」枠 (last_frame_*) とは別。
   *  次シーンへの「前の動画とつなげる」連鎖のアンカーに使う。 */
  video_last_frame_url?: string;
  video_last_frame_source_url?: string;
  video_last_frame_material_id?: string;
  narration_material_id?: string;
  narration_voice_id?: string;
  narration_duration_seconds?: number;
  // シーンの前面音声の種別。narration(既定, TTS) / sound_logo(ブランドのサウンドロゴ音声)。排他。
  audio_track_type?: 'narration' | 'sound_logo';
  sound_logo_material_id?: string;
  // サウンドロゴ素材の尺(読み取り時に素材の duration_seconds から導出。専用カラムは無い)。
  sound_logo_duration_seconds?: number;
  video_duration_seconds?: number;
  // 絵コンテのコマ尺 (秒)。1〜3 秒。ストーリーボードモードで AI 提案 + UI 編集。
  // GPT Image 2 のプロンプトに反映。動画クリップ尺 (video_duration_seconds) とは別。
  storyboard_duration_seconds?: number;
  video_has_audio?: boolean;
  // 「採用しない」トグル。素材紐付けは保持したまま、プレビューMP4合成や
  // エクスポート (ZIP/XML) の対象から外す。テイク選び直し/再生成で自動解除。
  video_excluded?: boolean;
  // 「前の動画とつなげる」トグル (動画カード単位の永続化・リロード復元用)。
  link_previous_video?: boolean;
  // シーンの動画入力方式の明示値 (P2)。'frame'|'extend'|'auto'。NULL = 未明示
  // (優先順位は link_previous_video 明示値 → §3.3 デフォルト導出。
  // docs/features/scene-location-and-production-mode-plan.md §3.2/§3.3.1)。
  // P2 step 2 時点では読み書きの土台のみ (書き込み UI は次 step)。
  // 'still' = 静止画をそのまま使う (動画を生成しない。features/scenarios/productionMode.ts isStillImageScene)。
  // 'motion' = モーショングラフィックスで作る (動画 AI ではなくコード生成。isMotionScene)。
  video_input_mode?: 'frame' | 'extend' | 'auto' | 'still' | 'motion' | null;
  // 「静止画をそのまま使う」シーンの余白の色 (#RRGGBB)。NULL = 既定の白 (resolveStillBackgroundColor)。
  still_background_color?: string | null;
  // 同トグルの参照元シーン明示指定 (§10 B案)。NULL = 直前の動画。
  link_previous_source_scene_id?: string | null;
  // 同トグルの静止画フレーム除外 (§11)。true = 参照動画のみでつなげる。
  link_previous_frame_excluded?: boolean;
  // 前面音声 (ナレーション/サウンドロゴ) の「採用しない」トグル。
  narration_excluded?: boolean;
  created_at: string;
  updated_at: string;
}

/** シナリオの由来 (`generation_metadata.origin`)。欠損は 'generated' 扱い。
 *  docs/features/scenario-script-import-plan.md §13.1。 */
export type ScenarioOrigin = 'generated' | 'imported' | 'blank';

/** 取り込んだ台本の情報 (`generation_metadata.imported_script`)。
 *  一覧 API では `text` を返さない (詳細 API のみ。§6.8)。 */
export type ImportedScriptMeta = {
  filename?: string | null;
  source_format?: 'markdown' | 'text' | 'docx' | string | null;
  char_count?: number | null;
  text?: string;
};

/** 行ごとの照合結果の状態 (§6.6.6)。 */
export type ImportVerificationLineStatus =
  | 'exact' | 'split' | 'normalized' | 'truncated' | 'dropped' | 'mismatch' | 'missing' | 'extra' | 'unverified';

export type ImportVerificationLine = {
  order: number;
  kind: 'dialogue' | 'narration' | 'telop' | string;
  status: ImportVerificationLineStatus | string;
  speaker_ref?: 'ok' | 'unverified' | 'n/a' | string;
  detail?: string;
};

export type ImportUserConfirmation = {
  user_uid: string;
  confirmed_at: string;
  content_hash: string;
  warnings_at_confirmation: string[];
};

/** 台本取り込みの照合結果 (§6.6.6 / §6.6.7)。overall / source_match / saved_match は BE が warnings から導出する。 */
export type ImportVerification = {
  overall: 'needs_review' | 'auto_checked' | 'user_verified';
  source_match: 'ref_ok' | '要確認';
  saved_match: 'ok' | 'warning';
  cut_count_match: boolean;
  uncovered_lines: { line: number; text: string }[];
  partial_lines: { line: number; unreferenced: string[] }[];
  lines: ImportVerificationLine[];
  warnings: string[];
  /** script_cuts の尺と保存された尺の不一致 (警告キー duration_mismatch。モデル都合の丸め duration_adjusted とは別)。 */
  duration_mismatches?: { order: number; script_seconds: number | null; saved_seconds: number | null }[];
  scope_note?: string;
  auto?: {
    computed_at: string;
    content_hash: string;
    stale: boolean;
    stale_at?: string | null;
    stale_reason?: string | null;
  } | null;
  user_confirmation: ImportUserConfirmation | null;
  confirmation_history?: ImportUserConfirmation[];
};

/** 取り込みレポート (`generation_metadata.import_report`)。一覧 API では `script_cuts` を返さない。 */
export type ImportReport = {
  unmapped_notes?: string[];
  /** プログラム検出 (原文の URL / SE / BGM を含む行)。BE は {line, text} で返す (旧形の文字列も許容)。 */
  detected_unmapped?: ({ line: number; text: string } | string)[];
  script_cuts?: unknown[];
  verification?: ImportVerification | null;
  duration_adjustments?: { order: number; script_seconds: number | null; saved_seconds: number | null }[];
  duration_filled?: { order: number; saved_seconds: number | null }[];
  notes?: string[];
};

/**
 * 他シーンの動画フレームへの@参照の記録 (`generation_metadata.scene_frame_mentions`の値)。
 * キーはフレーム素材ID (UUID)。挿入時に選んだテイク (video_material_id)に固定され、
 * 候補・チップ・BEのトークン解決はこの記録を正本にする
 * (docs/features/scene-video-frame-materials-visibility-plan.md §2.1)。
 */
export type SceneFrameMentionEntry = {
  /** 抽出元の動画素材 (挿入時に選んだテイク)。 */
  video_material_id: string;
  /** 挿入時の参照元シーン。 */
  scene_id: string;
  /** 挿入時のシーン番号 (表示・置換文用。並べ替え後もラベルは固定)。 */
  scene_order: number;
  position: 'first' | 'last';
  inserted_at: string;
  /** 最後に挿入 (新規・再利用とも)された時刻。整理 (GC)の保護期間の基準。 */
  touched_at: string;
};

export type SceneFrameMentionMap = Record<string, SceneFrameMentionEntry>;

/** シナリオ行の generation_metadata。既知キーのみ型付けし、他は従来どおり unknown で読む。 */
export type ScenarioGenerationMetadata = Record<string, unknown> & {
  origin?: ScenarioOrigin;
  imported_script?: ImportedScriptMeta | null;
  import_report?: ImportReport | null;
  /** 収録動画から (video-import-motion-scenario-plan.md §3.2)。受付時に固定した要旨と選択。形は components/generation/scenario/sourceVideo.ts */
  source_video?: Record<string, unknown> | null;
  cast_roles?: ScenarioCastRole[] | null;
  scene_frame_mentions?: SceneFrameMentionMap | null;
};

export interface Scenario {
  id: string;
  organization_id: string;
  user_id: string;
  source_type: 'asin' | 'custom' | 'manual';
  asin?: string;
  product_id?: string;
  title: string;
  // 生成ライフサイクル (ランダム生成のスケルトンシェル用)。通常は 'ready'。
  status?: 'generating' | 'ready' | 'failed';
  summary?: string;
  telop_style?: string;
  custom_input?: Record<string, unknown>;
  generation_metadata?: ScenarioGenerationMetadata;
  raw_response?: Record<string, unknown>;
  tags?: string[];
  /** 制作進捗 (organization_material_workflow_stages.id)。null / 未設定 = 未設定。
   *  ラベルは useMaterialWorkflowStages() の一覧から解決する (素材と共有のステージ定義)。
   *  docs/features/workflow-stage-take8-and-scenario-book-plan.md §3 */
  workflow_stage_id?: string | null;
  scenes?: Scene[];
  published: boolean;
  published_at?: string;
  starred: boolean;
  aspect_ratio?: string;
  frame_mode?: 'first_only' | 'first_and_last' | 'first_with_next' | 'reference' | 'storyboard';
  reference_image_urls?: Array<{ id: string; sourceUrl: string; thumbnailUrl: string }> | null;
  storyboards?: Storyboard[];
  bgm_material_id?: string;
  // BGM の「採用しない」トグル。素材紐付けは保持したまま合成/エクスポートから外す。
  bgm_excluded?: boolean;
  video_model?: string;
  output_resolution?: string;
  casting_model_ids?: string[];
  // Seedance プリセット俳優の割り当て (Seedance 系モデルのとき。casting_model_ids と排他)。
  // GET /scenarios の select 列に含まれる (scenario_crud.py)。
  seedance_preset_actor_ids?: string[] | null;
  // 登場人物数 (生成時に指定)。将来の複数 AI 俳優対応で参照。指定なし / 人物を含めない時は未設定。
  character_count?: number;
  casting?: {
    id: string;
    display_name: string;
    gender?: string;
    min_age?: number;
    max_age?: number;
    ethnicity?: string;
    face_image_public_url?: string;
    character_sheet_public_url?: string;
  };
  // 複数AI俳優対応: casting_model_ids 全件の俳優情報 (順序保持)。casting は casting_models[0] と同値。
  casting_models?: {
    id: string;
    display_name: string;
    gender?: string;
    min_age?: number;
    max_age?: number;
    ethnicity?: string;
    face_image_public_url?: string;
    character_sheet_public_url?: string;
  }[];
  deleted_at?: string;
  created_at: string;
  updated_at: string;
  /** AI俳優「入れ替え」の適用結果 (docs/features/scenario-actor-replace-plan.md §3.1)。
   *  PUT /api/generation/scenarios/<id> のレスポンスにのみ載る一時的なフィールドで、
   *  GET のシナリオ行には現れず DB にも保存されない。`apply_actor_replacements: true`
   *  を送った場合のみサーバーが返す。 */
  actor_replacement?: ScenarioActorReplacementResult;
}

/** サーバーが更新前後の配列差分から導出した位置置換1件 (§3.1.1)。kind は
 *  casting_model_ids 系 ('actor') か seedance_preset_actor_ids 系
 *  ('seedance_preset') かを表す。 */
export type ScenarioActorReplacementEntry = {
  position: number;
  from_id: string;
  to_id: string;
  kind: 'actor' | 'seedance_preset';
};

/** `apply_actor_replacements: true` で保存し、置換が適用されたシーンの書き換え後
 *  フィールド。差分があったフィールドのみ含まれる想定 (FE は返ってきたキーだけ
 *  上書きすればよい)。 */
export type ScenarioActorReplacedScene = {
  id: string;
  visual?: string;
  setting?: string;
  narration?: string;
  telop?: string;
  dialogue?: string;
  script?: string;
  dialogue_lines?: DialogueLine[] | null;
  telop_lines?: TelopLine[] | null;
  updated_at?: string;
};

export type ScenarioActorReplacementResult = {
  applied: boolean;
  /** applied: false のときの理由 (人数変更・並べ替え等で導出できなかった旨)。 */
  reason?: string;
  replacements?: ScenarioActorReplacementEntry[];
  replaced_scenes?: ScenarioActorReplacedScene[];
  /** from_id4 衝突などでトークンを書き換えられなかった警告 (§3.3)。 */
  warnings?: string[];
};

export interface ScenarioListItem {
  id: string;
  user_id: string;
  title: string;
  summary?: string;
  tags?: string[];
  source_type: 'asin' | 'custom' | 'manual';
  asin?: string;
  custom_input?: Record<string, unknown>;
  /** 一覧 API でも返る (台本の原文 text とカット配列 script_cuts は除外済み)。 */
  generation_metadata?: ScenarioGenerationMetadata | null;
  published: boolean;
  published_at?: string;
  starred: boolean;
  is_bookmarked?: boolean;
  bookmark_count?: number;
  scene_count?: number;
  aspect_ratio?: string;
  /** 制作進捗 (organization_material_workflow_stages.id)。一覧 API が返す。null = 未設定 */
  workflow_stage_id?: string | null;
  frame_mode?: 'first_only' | 'first_and_last' | 'first_with_next' | 'reference' | 'storyboard';
  reference_image_urls?: Array<{ id: string; sourceUrl: string; thumbnailUrl: string }> | null;
  scenes?: Array<{
    id: string;
    order_number: number;
    image_url?: string;
    thumbnail_url?: string;
    /** 一覧 API (list_scenarios) が scenes(...) の select に含めて返す素材 ID。
     *  シナリオブックの「動画エディタで開く」ボタンの表示判定 (動画素材の有無) に使う。 */
    image_material_id?: string | null;
    video_material_id?: string | null;
    /** video_material_id の素材サムネイル (temporary_materials.thumbnail_url)。
     *  絵コンテモードのカードで絵コンテの代わりに表示する。 */
    video_thumbnail_url?: string | null;
  }>;
  deleted_at?: string;
  created_at: string;
  updated_at: string;
}

export interface UpdateScenarioPayload {
  title?: string;
  summary?: string;
  tags?: string[];
  aspect_ratio?: string;
  frame_mode?: string;
  bgm_material_id?: string | null;
  /** BGM の「採用しない」トグル。true でプレビュー/エクスポートから除外 (紐付けは保持) */
  bgm_excluded?: boolean;
  video_model?: string;
  /** 制作進捗。null で未設定に戻す。省略時は既存値を維持 (差分方式)。
   *  BE は自組織で有効なステージ ID のみ受け付ける (それ以外は 400) */
  workflow_stage_id?: string | null;
  output_resolution?: string;
  reference_image_urls?: Array<{ id: string; sourceUrl: string; thumbnailUrl: string }> | null;
  /** AI俳優の差し替え (シナリオ情報モーダル)。順序 = 登場人物A/B/C。
   *  Seedance 系モデルでは seedance_preset_actor_ids を、それ以外では
   *  casting_model_ids を使う (排他)。モデルファミリーを跨いで変更した場合は
   *  使わない側を [] で明示クリアする。 */
  casting_model_ids?: string[];
  seedance_preset_actor_ids?: string[];
  /** AI俳優「入れ替え」opt-in フラグ (docs/features/scenario-actor-replace-plan.md §3.1)。
   *  true のとき、サーバーが更新前後の casting_model_ids / seedance_preset_actor_ids の
   *  配列差分から位置置換を導出し、シーン本文の @参照・話者帰属を追随させる。
   *  クライアントは置換マッピングそのものは送らない (サーバーが導出する)。
   *  未指定 / false: 従来どおり配列の丸ごと差し替えのみ (シーン書き換えなし)。 */
  apply_actor_replacements?: boolean;
  /** ユーザーが選択中素材として明示的に保持したい素材 ID の配列。
   *  null / 未指定: 保存済み選択無し (legacy)。auto-complete を発火する
   *  []: ユーザーが全部外した状態。auto-complete を抑制し空のまま復元
   *  バックエンドは generation_metadata.user_selected_asset_ids (JSONB) に保存する。
   *  サーバー側で `||` マージするため、他の generation_metadata キーを上書きしない。 */
  user_selected_asset_ids?: string[];
  /** 紐付けるブランド (brands.id)。ブランド新規作成直後の紐付けで使う。 */
  brand_id?: string;
  /** 絵コンテ生成トグル。バックエンドが generation_metadata へマージ保存する。 */
  avoid_faces?: boolean;
  illustration_storyboard?: boolean;
  include_storyboard_logo?: boolean;
  include_storyboard_product_image?: boolean;
}

export interface UpdateScenePayload {
  setting?: string;
  script?: string;
  narration?: string;
  dialogue?: string;
  /** 発話単位話者帰属の更新。list のみ受理 ([] = 全行削除)。null は BE が無視する
   *  (構造化→レガシーへ戻す操作は存在しない。docs/features/scenario-dialogue-speaker-plan.md §3.6)。
   *  構造化モードでは同時に dialogue / script を送らないこと (BE が無視して WARNING)。 */
  dialogue_lines?: DialogueLine[];
  telop?: string;
  /** テロップ行ごとの話者割り当ての更新。list のみ受理 ([] = 全行削除)。null は BE が無視する。
   *  telop と同時に送ると telop_lines が優先され telop は BE 導出値で上書きされる
   *  (docs/features/scenario-telop-speaker-plan.md §3.6)。 */
  telop_lines?: TelopLine[];
  telop_in_frame?: 'include' | 'exclude' | 'dialogue';
  visual?: string;
  image_url?: string;
  image_material_id?: string;
  /** null で動画の紐付けを解除する (ストーリーボードの動画カード削除など) */
  video_material_id?: string | null;
  image_prompt?: string;
  video_prompt?: string;
  generation_metadata?: Record<string, unknown>;
  last_frame_url?: string | null;
  last_frame_material_id?: string | null;
  /** シーン単位のフレーム生成の追加プロンプト。null / 空白 = 未設定へ戻す (BE で正規化・1000字まで)。 */
  frame_additional_prompt?: string | null;
  /** 参照画像の抜き差し。null = 未設定へ戻す。 */
  frame_reference_overrides?: SceneFrameReferenceOverrides | null;
  continues_previous_shot?: boolean;
  /** 場面固有化 (P1) の場面付け替え。シナリオの locations に対する allowlist 検証は
   *  サーバー側で行う (未宣言 key は 400)。null = 場面未設定へ戻す。
   *  docs/features/scene-location-and-production-mode-plan.md §2.1/§2.3。 */
  location_key?: string | null;
  has_visible_face?: boolean;
  video_duration_seconds?: number | null;
  storyboard_duration_seconds?: number | null;
  audio_track_type?: 'narration' | 'sound_logo';
  sound_logo_material_id?: string | null;
  narration_material_id?: string | null;
  /** シーン動画の「採用しない」トグル。true でプレビュー/エクスポートから除外 (紐付けは保持) */
  video_excluded?: boolean;
  /** 「前の動画とつなげる」トグル (動画カード単位の永続化) */
  link_previous_video?: boolean;
  /** シーンの動画入力方式の明示値 (P2)。null で未明示へ戻す。
   *  docs/features/scene-location-and-production-mode-plan.md §3.2。 */
  video_input_mode?: 'frame' | 'extend' | 'auto' | 'still' | 'motion' | null;
  /** 「静止画をそのまま使う」シーンの余白の色 (#RRGGBB)。null で既定の白へ戻す */
  still_background_color?: string | null;
  /** 同トグルの参照元シーン明示指定 (§10 B案)。null で「直前の動画」へ戻す */
  link_previous_source_scene_id?: string | null;
  /** 同トグルの静止画フレーム除外 (§11) */
  link_previous_frame_excluded?: boolean;
  /** 前面音声 (ナレーション/サウンドロゴ) の「採用しない」トグル */
  narration_excluded?: boolean;
  /** ブランドカット (タイトルカード/エンドカード) の表示秒数 (1〜10) */
  brand_cut_duration_seconds?: number | null;
}

/**
 * 生成済みモーションシーンの BGM 同期の鮮度 (GET /generation/scenarios/:id/motion/sync-status・
 * docs/features/brand-cut-motion-graphics-plan.md §10.4 / §15.4)。stale のとき reason に理由
 * (「BGM が差し替わった」「シーンの開始位置が動いた」等) が入る。
 */
export interface MotionSyncStatus {
  stale: boolean;
  reason: string | null;
  /** 全編モーションの計画 (motion_plan) と今の状態が合わないとき true。reason は「計画が古い: …」(§10.5) */
  planStale?: boolean;
  /** 声の鮮度 (Phase 2g・§10.7.3 / §10.7.4)。BGM の有無によらず計算される */
  voice?: MotionVoiceStatus;
}

/**
 * 声の鮮度 (GET /motion/sync-status の各シーンの voice)。
 * - videoStale: 映像を生成したときの声の版と今の声の版が違う (= 映像の再生成が必要)。reason に理由
 * - needsRegenerate: 今の台本・声の設定と、声の素材を作ったときの入力が違う (= 声の再生成が必要)。
 *   null は判定なし (声の共通層が未対応の旧 BE)
 */
export interface MotionVoiceStatus {
  videoStale: boolean;
  reason: string | null;
  needsRegenerate: boolean | null;
  needsRegenerateReason?: string | null;
}

// ============================================================================
// セリフとナレーションの入れ替え (POST /generation/scenarios/:id/script/swap-narration-dialogue)
// docs/features/scenario-narration-dialogue-swap-plan.md §3.7 / §3.9
// ============================================================================

/** セリフ→ナレーションの対象。all = 全セリフ行 / speaker = 指定した話者IDの行だけ (§3.3)。 */
export type NarrationDialogueSwapScope = 'all' | 'speaker';

/** ナレーション→セリフの行に付ける話者 (dialogue_lines[].speakerと同じ形・§3.4)。 */
export type NarrationDialogueSwapNarrationSpeaker = {
  label: string;
  actor_id: string | null;
  actor_kind: DialogueLineActorKind | null;
  character_key?: string | null;
};

/** 選択条件 (dry_runと実行で同じ値を送る)。 */
export interface NarrationDialogueSwapOptions {
  dialogue_scope: NarrationDialogueSwapScope;
  /** dialogue_scope='speaker'のとき必須 */
  dialogue_speaker_id?: string | null;
  /** dry_runでは省略可。requires_narration_speaker=trueのとき実行に必須 */
  narration_speaker?: NarrationDialogueSwapNarrationSpeaker | null;
}

/** シナリオ全体のセリフ行の話者集計 (dry_run応答speakers[])。 */
export interface NarrationDialogueSwapSpeaker {
  speaker_id: string;
  kind: 'actor' | 'character' | 'label' | 'unassigned';
  label: string;
  actor_id?: string | null;
  actor_kind?: DialogueLineActorKind | null;
  key?: string | null;
  line_count: number;
  scene_count: number;
}

export type NarrationDialogueSwapWarningCode =
  | 'excluded_lines_kept'
  | 'legacy_scene_speaker_ignored'
  | 'legacy_scene_dialogue_kept'
  | 'effective_voice_source_changed'
  | 'mixed_voice_sources'
  | 'audio_cleared';

export interface NarrationDialogueSwapSceneEntry {
  scene_id: string;
  order: number | null;
  moved_to_narration: number;
  moved_to_dialogue: number;
  resulting_lines: number;
  audio_cleared: boolean;
  voice_source_changes: Array<{ line_id?: string | null; index: number; from: string | null; to: string | null }>;
  /** 将来の警告コードも受け取れるようstringを許す */
  warnings: Array<NarrationDialogueSwapWarningCode | string>;
  excluded_lines_kept?: number;
}

/** 実行時にそのまま返す確認トークン (§3.7・§3.8)。 */
export interface NarrationDialogueSwapConfirmation {
  scenario_updated_at: string | null;
  scenes: Array<{ scene_id: string; updated_at: string | null }>;
  options_digest: string;
}

export interface NarrationDialogueSwapDryRun {
  dry_run: true;
  applicable: boolean;
  has_narration_to_move: boolean;
  requires_narration_speaker: boolean;
  speakers: NarrationDialogueSwapSpeaker[];
  scenes: NarrationDialogueSwapSceneEntry[];
  skipped: Array<{ scene_id: string; order: number | null; reason: 'brand_cut' | 'sound_logo' | 'no_text' | string }>;
  confirmation: NarrationDialogueSwapConfirmation;
}

/** 実行応答の更新後のシーン行。 */
export interface NarrationDialogueSwapUpdatedScene {
  id: string;
  updated_at?: string | null;
  narration?: string | null;
  dialogue?: string | null;
  script?: string | null;
  dialogue_lines?: DialogueLine[] | null;
  telop_lines?: TelopLine[] | null;
  narration_material_id?: string | null;
}

export interface NarrationDialogueSwapResult {
  applied: true;
  scenes: NarrationDialogueSwapUpdatedScene[];
  warnings: Array<{ scene_id: string; order: number | null; warnings: string[] }>;
}

/** POSTのbody。 */
export type NarrationDialogueSwapRequest = NarrationDialogueSwapOptions & {
  dry_run: boolean;
  confirmation?: NarrationDialogueSwapConfirmation;
};

/** エラー応答のbody (409 CONFIRMATION_STALE / 422 DIALOGUE_LINES_OVER_LIMIT等)。 */
export interface NarrationDialogueSwapErrorBody {
  error?: string;
  error_code?:
    | 'CONFIRMATION_STALE'
    | 'DIALOGUE_LINES_OVER_LIMIT'
    | 'NARRATION_SPEAKER_REQUIRED'
    | 'NARRATION_SPEAKER_INVALID'
    | 'NOTHING_TO_SWAP'
    | 'NOT_SUPPORTED_MOTION_V2'
    | 'SCENARIO_GENERATING'
    | string;
  [key: string]: unknown;
}
